import { type TAssignee } from 'features/assignee/types';
import { type TAttachment } from 'features/file/types';
import { type OPEN_CREATE_WORK_STATE_KEY } from 'features/work/constants';
import { type TPaginationQueryParams } from 'lib/rtkQuery/types';
import { type TCommentListItem } from 'shared/components/CommentsWidget/types';

export type TWorkListLocationState = {
  [OPEN_CREATE_WORK_STATE_KEY]?: boolean;
} | null;

export type TWorkState = {
  workDetail: TWorkDetail | null;
  workComments: TCommentListItem[] | null;
};

export enum WORK_STATUS {
  PENDING = 'PENDING',
  COMPLETE = 'COMPLETE',
  DELETED = 'DELETED',
}

export enum WORK_TYPE {
  ALL = 'ALL',
  OTHER = 'OTHER',
  ELECTRONIC = 'ELECTRONIC',
  DOCUMENT = 'DOCUMENT',
  POLICY = 'POLICY',
  DEPLOY = 'DEPLOY',
  INTERNAL = 'INTERNAL',
  CONSULT = 'CONSULT',
}

export type TWorkListItem = {
  code: string;
  title: string;
  created_at: string;
  due_date?: string;
  is_important: boolean;
  is_completed: boolean;
  is_read: boolean;
  handlers: TAssignee[];
  created_by: TAssignee;
};

export type TFetchWorksListBaseParams = TPaginationQueryParams & {
  work_type: WORK_TYPE;
  title?: string;
  status?: string;
  is_important?: boolean;
  is_completed?: boolean;
  is_unread?: boolean;
  handler?: string;
  created_by?: string;
};

export type TFetchWorksListURLParams = TFetchWorksListBaseParams & {
  date?: [string, string];
};

export type TFetchWorksListQueryParams = Omit<
  TFetchWorksListBaseParams,
  'handler' | 'created_by'
> & {
  from_date?: string;
  to_date?: string;
  assignee?: string[];
  related_work?: string;
};

export type TCreateWorkFormPayload = {
  work_type: WORK_TYPE;
  title: string;
  start_date: string;
  due_date?: string;
  attachments?: string[];
  content?: string;
  related_work?: string;
  related_documents?: { code: string; type: string }[];
};

export type TCreateAndUpdateWorkBasePayload = {
  title: string;
  start_date: string;
  due_date?: string;
  content: string;
  attachments: string[];
  work_type: WORK_TYPE;
  related_documents?: {
    code: string;
    type: string;
  }[];
  related_work?: string;
};

export type TCreateWorkPayload = TCreateAndUpdateWorkBasePayload & {
  assignees: TAssignee[];
};

export type TUpdateWorkPayload = {
  workCode: string;
  data: Omit<TCreateAndUpdateWorkBasePayload, 'attachments'> & {
    assignees: {
      new_items: TAssignee[];
      remove_items: TAssignee[];
    };
    attachments: {
      new_items: TAttachment[];
      remove_items: TAttachment[];
    };
  };
};

export type TRelatedWork = {
  code: string;
  title: string;
};

export type TWorkDetail = {
  code: string;
  title: string;
  content?: string;
  created_by: string;
  start_date: string;
  due_date?: string;
  is_completed: boolean;
  is_important: boolean;
  assignees: TAssignee[];
  attachments: TAttachment[];
  related_works?: TRelatedWork[];
};

export type TCreateWorkCommentPayload = {
  workCode: string;
  data: {
    content: string;
    file_ids: string[];
  };
};

export type TWorkVotesListItem = {
  assignee_code: string;
  name_uppercase: string;
  result: boolean | null;
};

export type TDeleteWorkCommentPayload = {
  workCode: string;
  commentId: string;
};

export type TMarkWorkAsImportantParams = {
  work_code: string;
  is_important: boolean;
};

export type TAssignMemberToWorkPayload = {
  work_code: string;
  due_date?: string;
  new_items: TAssignee[];
  remove_items: TAssignee[];
};

export type TUpdateWorkStatusPayload = {
  work_code: string;
  status: WORK_STATUS;
};

/** Replaces the whole related work list of a work — codes left out are unlinked */
export type TUpdateRelatedWorkPayload = {
  work_code: string;
  related_work: string[];
};
